import { zakelijkDoorCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import { OptionTemplate } from "shared/api/middleware-api-client";

export interface ConfiguredOptionPricingInput {
  selectedDoor: OptionTemplate | null;
  layout: OptionTemplate | null;
  color: OptionTemplate | null;
  glass: OptionTemplate | null;
  handle: OptionTemplate | null;
  extra: OptionTemplate[] | null;
}

export interface ConfiguredOptionPrices {
  layout: number;
  color: number;
  glass: number;
  handle: number;
  extras: number;
}

const getDoorElementCount = (selectedDoor: OptionTemplate | null): number =>
  selectedDoor?.configuration.nElements || 1;

export const getConfiguredLayoutPrice = (
  layout: OptionTemplate | null,
): number => layout?.price ?? 0;

export const getConfiguredColorPrice = (
  selectedDoor: OptionTemplate | null,
  color: OptionTemplate | null,
): number => {
  if (!selectedDoor || !color) return 0;

  if (zakelijkDoorCodes.includes(selectedDoor.code)) {
    return color.price;
  }

  const doorElements = getDoorElementCount(selectedDoor);
  const additionalDoorPrice = Math.max(0, color.price - 100);

  return color.price + Math.max(0, doorElements - 1) * additionalDoorPrice;
};

export const getConfiguredGlassPrice = (
  selectedDoor: OptionTemplate | null,
  glass: OptionTemplate | null,
): number => {
  if (!selectedDoor || !glass) return 0;

  return glass.price * getDoorElementCount(selectedDoor);
};

export const getConfiguredHandlePrice = (
  handle: OptionTemplate | null,
): number => handle?.price ?? 0;

export const getConfiguredExtrasPrice = (
  extra: OptionTemplate[] | null,
): number =>
  extra?.reduce(
    (sum: number, option: OptionTemplate) =>
      sum + option.price * (option.amount || 1),
    0,
  ) ?? 0;

export const getConfiguredOptionPrices = ({
  selectedDoor,
  layout,
  color,
  glass,
  handle,
  extra,
}: ConfiguredOptionPricingInput): ConfiguredOptionPrices => ({
  layout: getConfiguredLayoutPrice(layout),
  color: getConfiguredColorPrice(selectedDoor, color),
  glass: getConfiguredGlassPrice(selectedDoor, glass),
  handle: getConfiguredHandlePrice(handle),
  extras: getConfiguredExtrasPrice(extra),
});
